import React, {useEffect} from 'react';

import classNames from 'classnames';
import CSSModules from 'react-css-modules';

import styles from './styles.module.scss';

const IS_DIGIT_REGEX = /^\d+$/;

export type OtpEntryInputProps = {
  value: string;
  valueLength: number;
  onChange: (value: string) => void;
  hasError?: boolean;
};

interface OtpCredentialRequestOptions extends CredentialRequestOptions {
  otp: {
    transport: string[];
    code?: string;
  };
}

interface OtpCredential extends Credential {
  code: string;
}

const OtpEntryInput = ({
  value,
  valueLength,
  onChange,
  hasError,
}: OtpEntryInputProps): JSX.Element => {
  useEffect(() => {
    if ('OTPCredential' in window) {
      const ac = new AbortController();

      // Stop listening for OTP after 10 minutes
      setTimeout(
        () => {
          ac.abort();
        },
        10 * 60 * 1000
      );

      navigator.credentials
        .get({
          otp: {transport: ['sms']},
          signal: ac.signal,
        } as OtpCredentialRequestOptions)
        .then((otp) => {
          onChange((otp as OtpCredential)?.code);
          ac.abort();
        })
        .catch(() => {
          ac.abort();
        });
    }
  });

  const valueArray = value.split('');
  const numberList: string[] = [];

  for (let i = 0; i < valueLength; i++) {
    const number = valueArray[i];
    if (number !== undefined && IS_DIGIT_REGEX.test(number)) {
      numberList.push(number);
    } else {
      numberList.push('');
    }
  }

  const focusNextInput = (target: HTMLInputElement) => {
    const nextInput = target.nextElementSibling as HTMLInputElement;
    if (nextInput?.tagName === 'INPUT') {
      nextInput.focus();
    } else {
      target.blur();
    }
  };

  const focusPreviousInput = (target: HTMLInputElement) => {
    const previousInput = target.previousElementSibling as HTMLInputElement;
    if (previousInput) {
      previousInput.focus();
    }
  };

  // eslint-disable-next-line complexity
  const handleOnChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const {target} = event;
    let targetValue = target.value.trim(); // Remove whitespace if user pastes in a value
    const isDigit = IS_DIGIT_REGEX.test(targetValue);

    // Only allow numbers
    if (!isDigit && targetValue !== '') {
      return;
    }

    const nextInput = target.nextElementSibling as HTMLInputElement;
    const isLastInput = index === valueLength - 1;
    // Don't allow user to delete a number if the next input is filled (except for the last input)
    if (nextInput?.value !== '' && !isDigit && !isLastInput) {
      return;
    }

    targetValue = isDigit ? targetValue : ' ';

    if (targetValue.length === 1) {
      const newNumberList = [...numberList];

      newNumberList[index] = targetValue;
      onChange(newNumberList.join(''));

      if (!isDigit) {
        return;
      }

      focusNextInput(target);
    } else if (targetValue.length === valueLength) {
      // Only allow user to paste in values of correct length
      onChange(targetValue);
      target.blur();
    }
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const {key, currentTarget} = e;

    const writingDirection = getComputedStyle(currentTarget).direction;
    const moveForwardsKeys = [
      'ArrowDown',
      writingDirection === 'rtl' ? 'ArrowLeft' : 'ArrowRight',
    ];
    const moveBackwardsKeys = [
      'ArrowUp',
      writingDirection === 'rtl' ? 'ArrowRight' : 'ArrowLeft',
    ];

    if (
      moveForwardsKeys.includes(key) ||
      String(currentTarget.value) === key // Move to next input if user types the same number
    ) {
      e.preventDefault();
      focusNextInput(currentTarget);
      return;
    }
    if (moveBackwardsKeys.includes(key)) {
      e.preventDefault();
      focusPreviousInput(currentTarget);
      return;
    }

    // Ensure onChange is triggered when changing an existing value
    currentTarget.setSelectionRange(0, currentTarget.value.length);

    if (key !== 'Backspace' || currentTarget.value !== '') {
      return;
    }

    focusPreviousInput(currentTarget);
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const {target} = e;
    const previousInput = target.previousElementSibling as HTMLInputElement;
    if (previousInput && previousInput.value === '') {
      previousInput.focus();
      return;
    }

    // Select the number when an input is focused
    target.setSelectionRange(0, target.value.length);
  };

  return (
    <div
      styleName={classNames('wrapper', {error: hasError})}
      style={{'--num-inputs': valueLength} as React.CSSProperties}
    >
      {numberList.map((number, index) => (
        <input
          key={index}
          type='text'
          inputMode='numeric'
          autoComplete='one-time-code'
          pattern='[0-9]*'
          maxLength={valueLength}
          value={number}
          onChange={(e) => handleOnChange(e, index)}
          onKeyDown={handleOnKeyDown}
          onFocus={handleOnFocus}
          data-automation='otp-code-input'
        />
      ))}
    </div>
  );
};

export default CSSModules(OtpEntryInput, styles, {allowMultiple: true});
